import React, { useEffect, useState, useCallback } from 'react';
import api from '../api';
import { formatFraction } from '../utils/fractions';
import { formatCurrency } from '../utils/currency';

// ── Constants ─────────────────────────────────────────────────────────────────

const BOARD_STAGES = [
  { value: 'order_received',     label: 'Order Received',     color: '#6b7280', bg: 'rgba(107,114,128,0.08)', next: 'awaiting_materials' },
  { value: 'awaiting_materials', label: 'Awaiting Materials', color: '#3b82f6', bg: 'rgba(59,130,246,0.09)',  next: 'in_production' },
  { value: 'in_production',      label: 'In Production',      color: '#f59e0b', bg: 'rgba(245,158,11,0.09)', next: 'quality_check' },
  { value: 'quality_check',      label: 'Quality Check',      color: '#8b5cf6', bg: 'rgba(139,92,246,0.09)', next: 'ready_for_dispatch' },
  { value: 'ready_for_dispatch', label: 'Ready for Dispatch', color: '#10b981', bg: 'rgba(16,185,129,0.09)', next: 'delivered' },
  { value: 'delivered',          label: 'Delivered',          color: '#4b5563', bg: 'rgba(75,85,99,0.05)',   next: null },
];

const ARCHIVED_STAGE = { value: 'archived', label: 'Archived', color: '#4b5563', bg: 'rgba(75,85,99,0.04)', next: null };
const PRIORITY_COLORS = { urgent: '#ef4444', high: '#f59e0b', normal: null, low: '#9ca3af' };

const STATUSES = [
  { value: 'order_received',     label: 'Order Received' },
  { value: 'awaiting_materials', label: 'Awaiting Materials' },
  { value: 'in_production',      label: 'In Production' },
  { value: 'quality_check',      label: 'Quality Check' },
  { value: 'ready_for_dispatch', label: 'Ready for Dispatch' },
  { value: 'delivered',          label: 'Delivered' },
  { value: 'on_hold',            label: 'On Hold' },
  { value: 'invoiced',           label: 'Invoiced' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low' }, { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' }, { value: 'urgent', label: 'Urgent' },
];

const EMPTY_FORM = {
  job_number: '', customer_id: null, quote_id: null,
  title: '', description: '', value: '',
  status: 'order_received', priority: 'normal',
  due_date: '', notes: ''
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtCurr(v) { return v != null ? formatCurrency(parseFloat(v)) : '—'; }

function PriorityDot({ priority }) {
  const color = PRIORITY_COLORS[priority];
  if (!color) return null;
  return <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0, verticalAlign: 'middle' }} />;
}

// ── Edit Modal (used by full board only) ──────────────────────────────────────

function ProjectModal({ project, customers, quotes, onClose, onSaved }) {
  const [form, setForm] = useState(project ? { ...project, value: project.value || '' } : EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const handleQuoteSelect = (quoteId) => {
    if (!quoteId) { set('quote_id', null); return; }
    const q = quotes.find(q => q.id === parseInt(quoteId));
    if (q) setForm(prev => ({ ...prev, quote_id: q.id, customer_id: q.customer_id || null, title: q.quote_number || `Quote #${q.id}`, value: q.value || '' }));
  };

  const save = async () => {
    if (!form.job_number.trim()) return setError('Job number is required');
    if (!form.title.trim())      return setError('Title is required');
    setSaving(true);
    try {
      const payload = {
        ...form,
        quote_id:    form.quote_id    ? parseInt(form.quote_id)    : null,
        customer_id: form.customer_id ? parseInt(form.customer_id) : null,
        value:       form.value !== '' ? parseFloat(form.value)    : null,
        due_date:    form.due_date || null,
      };
      await api.patch(`/projects/${project.id}`, payload);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setSaving(false);
    }
  };

  const selQuote    = quotes.find(q => q.id === form.quote_id);
  const selCustomer = customers.find(c => c.id === form.customer_id);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 620 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Edit Project</span>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}

          <div className="form-group">
            <label>Quote (optional)</label>
            <select value={form.quote_id || ''} onChange={e => handleQuoteSelect(e.target.value)}>
              <option value="">Select quote...</option>
              {quotes.map(q => (
                <option key={q.id} value={q.id}>
                  {q.quote_number || `Quote #${q.id}`}
                  {q.customer_name ? ` — ${q.customer_name}` : ''}
                  {q.value ? ` (${formatCurrency(parseFloat(q.value))})` : ''}
                </option>
              ))}
            </select>
          </div>

          {selQuote && (
            <div style={{ background: 'var(--bg-primary)', border: '1px solid var(--accent)', borderRadius: 6, padding: '10px 14px', marginBottom: 16, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div>
                <div style={{ fontSize: 9, color: 'var(--text-muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 3 }}>Customer</div>
                <div style={{ fontSize: 13 }}>{selCustomer?.company_name || selQuote.customer_name || '—'}</div>
              </div>
              <div>
                <div style={{ fontSize: 9, color: 'var(--text-muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 3 }}>Value</div>
                <div style={{ fontSize: 13, color: 'var(--accent)', fontFamily: 'var(--font-mono)' }}>
                  {form.value != null && form.value !== '' ? formatCurrency(parseFloat(form.value)) : '—'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 9, color: 'var(--text-muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 3 }}>Quote Status</div>
                <div style={{ fontSize: 13 }}>{selQuote.status || '—'}</div>
              </div>
            </div>
          )}

          <div className="form-grid">
            <div className="form-group">
              <label>Job Number *</label>
              <input value={form.job_number} onChange={e => set('job_number', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Priority</label>
              <select value={form.priority} onChange={e => set('priority', e.target.value)}>
                {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Title *</label>
            <input value={form.title} onChange={e => set('title', e.target.value)} />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea value={form.description || ''} onChange={e => set('description', e.target.value)} />
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label>Status</label>
              <select value={form.status} onChange={e => set('status', e.target.value)}>
                {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Due Date</label>
              <input type="date" value={form.due_date || ''} onChange={e => set('due_date', e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label>Notes</label>
            <textarea value={form.notes || ''} onChange={e => set('notes', e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Line Items Panel ──────────────────────────────────────────────────────────

function LineItemsPanel({ projectId, data, onFetch }) {
  const [open, setOpen] = useState(false);
  const { loaded, loading, lines = [] } = data || {};

  const toggle = () => {
    if (!open && !loaded && !loading) onFetch(projectId);
    setOpen(o => !o);
  };

  return (
    <div style={{ marginTop: 10 }}>
      <button onClick={toggle} style={{
        background: 'none', border: 'none', cursor: 'pointer',
        color: 'var(--text-muted)', fontSize: 12,
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '4px 0', minHeight: 44, width: '100%', textAlign: 'left',
      }}>
        <span style={{ fontSize: 10 }}>{open ? '▼' : '▶'}</span>
        <span>Line items{loaded ? ` (${lines.length})` : ''}</span>
        {loading && <span style={{ fontSize: 10 }}>Loading...</span>}
      </button>
      {open && loaded && (
        <div style={{ paddingLeft: 4 }}>
          {lines.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-muted)', paddingLeft: 12 }}>No line items</div>}
          {lines.map((li, i) => (
            <div key={li.id} style={{ borderLeft: '2px solid var(--border)', paddingLeft: 10, marginBottom: 8 }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent)' }}>
                P{i + 1} · {li.location_description || '—'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span>{formatFraction(li.width_inches)} × {formatFraction(li.height_inches)}</span>
                <span>· {li.quantity || 1} unit{(li.quantity || 1) !== 1 ? 's' : ''}</span>
                {li.is_frosted && (
                  <span style={{ background: 'rgba(59,130,246,0.15)', color: '#3b82f6', fontSize: 9, padding: '1px 5px', borderRadius: 3 }}>Frosted</span>
                )}
              </div>
              {li.notes && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2, fontStyle: 'italic' }}>{li.notes}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Project Card Content ──────────────────────────────────────────────────────

function ProjectCardContent({ project, readOnly, isArchived, onMove, onArchive, onUnarchive, onEdit, linesData, onFetchLines }) {
  const stageConfig = BOARD_STAGES.find(s => s.value === project.status);
  const overdue = project.due_date && new Date(project.due_date) < new Date() && project.status !== 'delivered';
  const nextStage = stageConfig?.next ? BOARD_STAGES.find(s => s.value === stageConfig.next) : null;

  return (
    <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', background: 'var(--bg-primary)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '8px 16px', marginBottom: 10 }}>
        {project.customer_name && (
          <div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>Customer</div>
            <div style={{ fontSize: 13 }}>{project.customer_name}</div>
          </div>
        )}
        {project.title && (
          <div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>Title</div>
            <div style={{ fontSize: 13 }}>{project.title}</div>
          </div>
        )}
        <div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>Due Date</div>
          <div style={{ fontSize: 13, color: overdue ? 'var(--danger)' : 'var(--text-primary)' }}>
            {project.due_date ? new Date(project.due_date).toLocaleDateString('en-GB') : '—'}
            {overdue && ' ⚠'}
          </div>
        </div>
        {!readOnly && project.value && (
          <div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>Value</div>
            <div style={{ fontSize: 13, fontFamily: 'var(--font-mono)', color: 'var(--accent)' }}>{fmtCurr(project.value)}</div>
          </div>
        )}
        {project.unit_count != null && Number(project.unit_count) > 0 && (
          <div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>Units</div>
            <div style={{ fontSize: 13 }}>{project.unit_count}</div>
          </div>
        )}
      </div>

      {project.notes && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10, fontStyle: 'italic' }}>{project.notes}</div>
      )}

      <LineItemsPanel projectId={project.id} data={linesData} onFetch={onFetchLines} />

      {!readOnly && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          {!isArchived && nextStage && (
            <button className="btn btn-primary btn-sm" style={{ minHeight: 44 }}
              onClick={() => onMove(project.id, nextStage.value)}>
              Move to {nextStage.label} →
            </button>
          )}
          {!isArchived && project.status === 'delivered' && (
            <button className="btn btn-secondary btn-sm" style={{ minHeight: 44 }}
              onClick={() => onArchive(project.id)}>
              Archive
            </button>
          )}
          {isArchived && (
            <button className="btn btn-secondary btn-sm" style={{ minHeight: 44 }}
              onClick={() => onUnarchive(project.id)}>
              Unarchive
            </button>
          )}
          {onEdit && !isArchived && (
            <button className="btn btn-secondary btn-sm" style={{ minHeight: 44, marginLeft: 'auto' }}
              onClick={() => onEdit(project)}>
              Edit
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Stage Band ────────────────────────────────────────────────────────────────

function StageBand({ stage, projects, readOnly, isExpanded, onToggle, isArchived, onMove, onArchive, onUnarchive, onEdit, expandedCards, onToggleCard, linesCache, onFetchLines }) {
  return (
    <div>
      <div onClick={onToggle} style={{
        position: 'sticky', top: 0, zIndex: 5,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 16px', minHeight: 44, cursor: 'pointer',
        background: stage.bg,
        borderLeft: `3px solid ${stage.color}`,
        borderBottom: '1px solid var(--border)',
        borderTop: '1px solid var(--border)',
        backdropFilter: 'blur(4px)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', background: stage.color, display: 'inline-block', flexShrink: 0 }} />
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', color: stage.color }}>
            {stage.label}
          </span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)', background: 'var(--bg-card)', padding: '1px 7px', borderRadius: 10 }}>
            {projects.length}
          </span>
        </div>
        <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{isExpanded ? '▲' : '▼'}</span>
      </div>

      {isExpanded && (
        <>
          {projects.length === 0 && (
            <div style={{ padding: '10px 20px', color: 'var(--text-muted)', fontSize: 12, fontStyle: 'italic', borderBottom: '1px solid var(--border)' }}>
              No jobs at this stage
            </div>
          )}
          {projects.map(p => {
            const dotColor = PRIORITY_COLORS[p.priority];
            const isCardOpen = !!expandedCards[p.id];
            return (
              <div key={p.id} style={{ borderBottom: '1px solid var(--border)' }}>
                <div onClick={() => onToggleCard(p.id)} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '0 16px', minHeight: 44, cursor: 'pointer',
                  background: isCardOpen ? 'var(--bg-primary)' : 'var(--bg-card)',
                }}>
                  {dotColor
                    ? <span style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
                    : <span style={{ width: 8, flexShrink: 0 }} />
                  }
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--accent)', flexShrink: 0 }}>{p.job_number}</span>
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.customer_name || p.title}
                  </span>
                  <span style={{ color: 'var(--text-muted)', fontSize: 11, flexShrink: 0 }}>{isCardOpen ? '▲' : '▼'}</span>
                </div>
                {isCardOpen && (
                  <ProjectCardContent
                    project={p}
                    readOnly={readOnly}
                    isArchived={isArchived}
                    onMove={onMove}
                    onArchive={onArchive}
                    onUnarchive={onUnarchive}
                    onEdit={isArchived ? null : onEdit}
                    linesData={linesCache[p.id]}
                    onFetchLines={onFetchLines}
                  />
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

// ── KanbanBoard (default export) ──────────────────────────────────────────────

/**
 * Self-contained Kanban board component.
 *
 * readOnly={false} (default — Projects page):
 *   Full functionality: Move stage, Archive, Unarchive, Edit, Show Archived toggle.
 *
 * readOnly={true} (ShopFloor Jobs tab):
 *   Read-only view. Delivered and archived jobs hidden.
 *   Cards expand to show details and line items. No action buttons.
 */
export default function KanbanBoard({ readOnly = false }) {
  const [projects,        setProjects]        = useState([]);
  const [archivedProjects,setArchivedProjects]= useState([]);
  const [loading,         setLoading]         = useState(true);
  const [showArchived,    setShowArchived]     = useState(false);
  const [loadingArchived, setLoadingArchived]  = useState(false);
  const [expandedBands,   setExpandedBands]   = useState(() => {
    const init = {};
    BOARD_STAGES.forEach(s => { init[s.value] = s.value !== 'delivered'; });
    init['archived'] = true;
    return init;
  });
  const [expandedCards,   setExpandedCards]   = useState({});
  const [linesCache,      setLinesCache]      = useState({});
  const [editing,         setEditing]         = useState(null);
  const [customers,       setCustomers]       = useState([]);
  const [quotes,          setQuotes]          = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/projects')
      .then(r => { setProjects(r.data.value || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    if (!readOnly) {
      api.get('/customers').then(r => setCustomers(r.data.value || [])).catch(() => {});
      api.get('/quotes').then(r => setQuotes(r.data.value || [])).catch(() => {});
    }
  }, [load, readOnly]);

  const fetchLines = useCallback((projectId) => {
    if (linesCache[projectId]?.loaded || linesCache[projectId]?.loading) return;
    setLinesCache(prev => ({ ...prev, [projectId]: { loaded: false, loading: true, lines: [] } }));
    api.get(`/projects/${projectId}/lines`)
      .then(res => setLinesCache(prev => ({ ...prev, [projectId]: { loaded: true, loading: false, lines: res.data.value || [] } })))
      .catch(() => setLinesCache(prev => ({ ...prev, [projectId]: { loaded: true, loading: false, lines: [] } })));
  }, [linesCache]);

  const handleMove = async (projectId, nextStatus) => {
    try {
      await api.patch(`/projects/${projectId}`, { status: nextStatus });
      setProjects(prev => prev.map(p => p.id === projectId ? { ...p, status: nextStatus } : p));
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const handleArchive = async (projectId) => {
    try {
      await api.patch(`/projects/${projectId}`, { archived: true });
      setProjects(prev => prev.filter(p => p.id !== projectId));
      setExpandedCards(prev => { const n = { ...prev }; delete n[projectId]; return n; });
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const handleUnarchive = async (projectId) => {
    try {
      await api.patch(`/projects/${projectId}`, { archived: false });
      const unarchived = archivedProjects.find(p => p.id === projectId);
      setArchivedProjects(prev => prev.filter(p => p.id !== projectId));
      if (unarchived) setProjects(prev => [{ ...unarchived, archived: false }, ...prev]);
      setExpandedCards(prev => { const n = { ...prev }; delete n[projectId]; return n; });
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const handleToggleArchived = async () => {
    if (!showArchived && archivedProjects.length === 0) {
      setLoadingArchived(true);
      try {
        const res = await api.get('/projects?archived=true');
        setArchivedProjects(res.data.value || []);
      } catch {}
      setLoadingArchived(false);
    }
    setShowArchived(s => !s);
  };

  const handleToggleBand   = (v) => setExpandedBands(prev => ({ ...prev, [v]: !prev[v] }));
  const handleToggleCard   = (id) => setExpandedCards(prev => ({ ...prev, [id]: !prev[id] }));

  // readOnly: hide delivered stage and filter delivered/archived projects out
  const visibleStages   = readOnly ? BOARD_STAGES.filter(s => s.value !== 'delivered') : BOARD_STAGES;
  const visibleProjects = readOnly ? projects.filter(p => p.status !== 'delivered' && !p.archived) : projects;

  if (loading) return <div className="loading">Loading jobs...</div>;

  return (
    <div>
      <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
        {visibleStages.map(stage => (
          <StageBand
            key={stage.value}
            stage={stage}
            projects={visibleProjects.filter(p => p.status === stage.value)}
            readOnly={readOnly}
            isExpanded={expandedBands[stage.value] !== false}
            onToggle={() => handleToggleBand(stage.value)}
            isArchived={false}
            onMove={handleMove}
            onArchive={handleArchive}
            onUnarchive={handleUnarchive}
            onEdit={readOnly ? null : setEditing}
            expandedCards={expandedCards}
            onToggleCard={handleToggleCard}
            linesCache={linesCache}
            onFetchLines={fetchLines}
          />
        ))}

        {!readOnly && showArchived && (
          <StageBand
            stage={ARCHIVED_STAGE}
            projects={archivedProjects}
            readOnly={false}
            isExpanded={expandedBands['archived'] !== false}
            onToggle={() => handleToggleBand('archived')}
            isArchived={true}
            onMove={handleMove}
            onArchive={handleArchive}
            onUnarchive={handleUnarchive}
            onEdit={null}
            expandedCards={expandedCards}
            onToggleCard={handleToggleCard}
            linesCache={linesCache}
            onFetchLines={fetchLines}
          />
        )}
      </div>

      {!readOnly && (
        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            className={`btn btn-sm ${showArchived ? 'btn-primary' : 'btn-secondary'}`}
            onClick={handleToggleArchived}
            disabled={loadingArchived}
          >
            {loadingArchived ? 'Loading...' : showArchived ? '✓ Hide Archived' : 'Show Archived'}
          </button>
          {showArchived && !loadingArchived && (
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{archivedProjects.length} archived</span>
          )}
        </div>
      )}

      {editing && (
        <ProjectModal
          project={editing}
          customers={customers}
          quotes={quotes}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}
